<script setup lang="ts">
import type { AccordionItem, FormSubmitEvent } from '#ui/types'
import { merge } from 'es-toolkit/compat'

const props = defineProps<{
  action: 'create' | 'edit'
  data: Partial<Salesorder>
  onSubmit: (event: FormSubmitEvent<Partial<Salesorder>>) => void
  openNestedCallback: (data: Partial<Salesorder>) => void
  title?: string
  disabledFields?: (keyof Salesorder | string)[]
  readonly?: boolean
  navigateToSubpage?: boolean
}>()

const router = useRouter()
const { user } = useUserSession()
const { moduleId, userModule } = useModules()
const slideover = useSlideover()

const inert = computed(() => props.readonly || props.data?.immutable || !['admin', 'edit'].includes(userModule.value?.role))
const simple = ref(true)

const state = ref<Partial<Salesorder>>(getDefaultSalesorder(props.data))

const salesorders$ = useSalesorders()

const $accounts = await useFetch<Account[]>('/api/db/accounts', {
  query: { },
  default: () => [],
})
const $branches = useFetch<Branch[]>('/api/db/branches', {
  query: () => ({ $columns: ['id', 'image', 'name', 'address'] }),
  default: () => [],
})
const $contacts = useFetch<Contact[]>('/api/db/contacts', {
  query: () => ({ type: 'sales', $columns: ['id', 'name', 'accountId', 'title', 'email', 'phone'] }),
  default: () => [],
})
const $deals = await useFetch<Deal[]>('/api/db/deals', {
  query: { $with: { account: true } },
  default: () => [],
})
const $quotes = await useFetch<Quote[]>('/api/db/quotes', {
  query: { $with: { account: true } },
  default: () => [],
})
const $users = await useFetch<User[]>('/api/db/users', {
  query: { type: ['internal'], $columns: ['id', 'name', 'image', 'email'] },
  default: () => [],
})

const utils = useSalesordersUtils({ $accounts, $branches, $contacts, $deals, $quotes, $users })

const items = computed<AccordionItem[]>(() => {
  if (['create'].includes(props.action) && simple.value) {
    return [{ slot: 'simple', label: 'Creación rápida', icon: 'i-mdi-lightning-bolt', defaultOpen: true }]
  }
  return [
    {
      slot: 'general',
      label: 'General',
      icon: 'i-mdi-information',
      defaultOpen: true,
    },
    {
      slot: 'details',
      label: 'Detalles',
      icon: ICONS.salesorders,
      defaultOpen: true,
    },
    {
      slot: 'payment',
      label: 'Facturación y condiciones de pago',
      icon: 'i-mdi-credit-card',
      defaultOpen: true,
    },
    {
      slot: 'delivery',
      label: 'Entrega',
      icon: 'i-mdi-truck-delivery',
      defaultOpen: true,
    },
    {
      slot: 'data',
      label: 'Datos adicionales',
      icon: 'i-mdi-code-json',
      defaultOpen: false,
    },
    {
      slot: 'pdfSettings',
      label: 'Configuración del PDF',
      icon: 'i-mdi-file-pdf',
      defaultOpen: false,
    },
  ].filter(item => !['data'].includes(item.slot) || state.value.data)
})

const onUpdateAccountId = async (id: string) => {
  const account = utils.maps.accountId.value.get(id) as Account
  if (!account) return
  state.value.receiver = { ...state.value.receiver, ...account.billing } as Salesorder['receiver']
  state.value.payment = { ...state.value.payment, ...account.payment } as Salesorder['payment']
  if (state.value.delivery!.useAccountAddress) {
    state.value.delivery!.address = account.billing?.address as Address
  }
}

const onUpdateQuoteId = (quoteId: string) => {
  const quote = utils.maps.quoteId.value.get(quoteId) as Quote
  if (!quote || !quote.payment) return
  const {
    name,
    userId,
    issuer,
    receiver,
    delivery,
    accountId,
    branchId,
    contactId } = quote

  const {
    options,
    ...paymentData
  } = quote.payment

  state.value.userId = userId
  state.value.payment = paymentData
  state.value.receiver = receiver
  state.value.issuer = issuer
  state.value.delivery = delivery
  state.value.accountId = accountId
  state.value.branchId = branchId
  state.value.contactId = contactId
}

if (['create'].includes(props.action) && props.data.quoteId) {
  const quote = utils.maps.quoteId.value.get(props.data.quoteId) as Quote
  state.value.dealId = quote.dealId
  onUpdateQuoteId(props.data.quoteId)
}

const accountAlert = computed(() => {
  if (!state.value.accountId) return false
  const deal = utils.maps.dealId.value.get(state.value.dealId) as Deal
  return deal?.accountId !== state.value.accountId
})

const loading = ref(false)

const onSubmitSalesorder = async (event: FormSubmitEvent<Partial<Salesorder>>) => {
  event.data = merge(state.value, event.data) // WORKAROUND: UForm drops nested props on validation
  loading.value = true

  if (['pending', 'EXW'].includes(event.data?.delivery!.incoterm)) {
    delete event.data.branchId
    event.data.delivery!.address = {} as Address
  }
  if (event.data?.delivery?.useAccountAddress) delete event.data.branchId

  await props.onSubmit(event)
  props.navigateToSubpage && router.push(`/sales/salesorders/${event.data.id}`)
}

const schema = computed(() => ['pending', 'EXW'].includes(state.value.delivery.incoterm)
  ? salesorders$.schemaPendingDelivery
  : salesorders$.schemaWithDelivery,
)
</script>

<template>
  <UDashboardSlideover prevent-close :ui="{ width: 'min-w-[40vw]' }">
    <template #title>
      <SlideoverTitle :title="title" :inert="inert" />
    </template>
    <UForm
      id="form.salesorders"
      :validate-on="['submit']"
      :schema="schema"
      :state="state"
      :inert="inert"
      @error="onFormError"
      @submit="onSubmitSalesorder"
    >
      <UAccordion
        multiple
        :items="items"
        :ui="{ item: { base: 'py-1 px-2 space-y-2 text-sm' } }"
      >
        <template #simple>
          <UFormGroup
            label="Nombre"
            name="name"
            :required="isRequired(schema, 'name')"
            :hint="state.uid"
          >
            <UButtonGroup class="flex">
              <InputText
                v-model.defaultcase="state.name"
                :disabled="disabledFields?.includes('name')"
                autofocus
                class="flex-1"
              />
              <ButtonGenerateString @click="state.name = state.uid" />
            </UButtonGroup>
          </UFormGroup>

          <UFormGroup
            label="Negocio"
            name="dealId"
            :required="isRequired(schema, 'dealId')"
          >
            <SelectMenuBaseReference
              v-model="state"
              clearable
              v-bind="{
                tableName: 'deals',
                idField: 'dealId',
                utils: utils,
                options: utils.options.dealId.value,
                disabled: disabledFields?.includes('dealId'),
                slideoverOpenCreateProps: {
                  data: { accountId: state.accountId, userId: state.userId },
                  disabledFields: [],
                },
                refresh: $deals.refresh,
                openNestedCallback: (data) => openNestedCallback({ ...props, data }).then(refresh),
              }"
            />
          </UFormGroup>

          <UFormGroup
            label="Cotización"
            name="quoteId"
            :required="isRequired(schema, 'quoteId')"
          >
            <SelectMenuBaseReference
              v-model="state"
              clearable
              v-bind="{
                tableName: 'quotes',
                idField: 'quoteId',
                utils: utils,
                options: utils.maps.quoteByDealId.value.get(state.dealId)?.map(toOption) || [],
                disabled: disabledFields?.includes('quoteId') || !state.dealId,
                onClickUpdate: onUpdateQuoteId,
                slideoverOpenCreateProps: {
                  data: { accountId: state.accountId, dealId: state.dealId, userId: state.userId },
                  disabledFields: ['dealId'],
                },
                refresh: $quotes.refresh,
                openNestedCallback: (data) => openNestedCallback({ ...props, data }).then(refresh),
              }"
            />
          </UFormGroup>

          <UFormGroup
            label="Comprobante"
            name="fileProofPdf"
            :required="isRequired(schema, 'fileProofPdf')"
          >
            <InputFile
              v-model="state.fileProofPdf"
              table-name="salesorders"
              :record-id="state.id"
              filename="comprobante.pdf"
              accept="application/pdf"
              :disabled="disabledFields?.includes('fileProofPdf')"
            />
          </UFormGroup>

          <SlideoverButtonExpand @click="simple = false" />
        </template>

        <template #general>
          <UFormGroup
            label="Nombre"
            name="name"
            :required="isRequired(schema, 'name')"
            :hint="state.uid"
          >
            <UButtonGroup class="flex">
              <InputText
                v-model.defaultcase="state.name"
                :disabled="disabledFields?.includes('name')"
                autofocus
                class="flex-1"
              />
              <ButtonGenerateString @click="state.name = state.uid" />
            </UButtonGroup>
          </UFormGroup>

          <UFormGroup
            label="Negocio"
            name="dealId"
            :required="isRequired(schema, 'dealId')"
          >
            <SelectMenuBaseReference
              v-model="state"
              clearable
              v-bind="{
                tableName: 'deals',
                idField: 'dealId',
                utils: utils,
                options: utils.options.dealId.value,
                disabled: disabledFields?.includes('dealId'),
                slideoverOpenCreateProps: {
                  data: { accountId: state.accountId, userId: state.userId },
                  disabledFields: [],
                },
                refresh: $deals.refresh,
                openNestedCallback: (data) => openNestedCallback({ ...props, data }).then(refresh),
              }"
            />
          </UFormGroup>

          <UFormGroup
            label="Cotización"
            name="quoteId"
            :required="isRequired(schema, 'quoteId')"
          >
            <SelectMenuBaseReference
              v-model="state"
              clearable
              v-bind="{
                tableName: 'quotes',
                idField: 'quoteId',
                utils: utils,
                options: utils.maps.quoteByDealId.value.get(state.dealId)?.map(toOption) || [],
                disabled: disabledFields?.includes('quoteId') || !state.dealId,
                onClickUpdate: onUpdateQuoteId,
                slideoverOpenCreateProps: {
                  data: { accountId: state.accountId, dealId: state.dealId, userId: state.userId },
                  disabledFields: ['dealId'],
                },
                refresh: $quotes.refresh,
                openNestedCallback: (data) => openNestedCallback({ ...props, data }).then(refresh),
              }"
            />
          </UFormGroup>

          <UFormGroup
            label="Cuenta"
            name="accountId"
            required
          >
            <SelectMenuBaseReference
              v-model="state"
              clearable
              v-bind="{
                tableName: 'accounts',
                idField: 'accountId',
                utils: utils,
                options: utils.options.accountId.value,
                disabled: disabledFields?.includes('accountId'),
                onClickUpdate: onUpdateAccountId,
                slideoverOpenCreateProps: {},
                refresh: $accounts.refresh,
                openNestedCallback: (data) => openNestedCallback({ ...props, data }).then(refresh),
              }"
            />
          </UFormGroup>

          <AppAlert
            v-if="accountAlert"
            type="warning"
            description="La cuenta seleccionada no coincide con la cuenta del negocio."
          />

          <UFormGroup
            label="Contacto"
            name="contactId"
            :required="isRequired(schema, 'contactId')"
          >
            <SelectMenuBaseReference
              v-model="state"
              clearable
              v-bind="{
                tableName: 'contacts',
                idField: 'contactId',
                utils: utils,
                options: utils.maps.contactsByAccountId.value.get(state.accountId)?.map(toOption) ?? [],
                disabled: disabledFields?.includes('contactId') || !state.accountId,
                slideoverOpenCreateProps: {
                  data: { moduleId: 'sales', accountId: state.accountId, branchId: state.branchId, userId: state.userId },
                  disabledFields: ['accountId'],
                },
                refresh: $contacts.refresh,
                openNestedCallback: (data) => openNestedCallback({ ...props, data }).then(refresh),
              }"
            />
          </UFormGroup>

          <UFormGroup
            label="Responsable"
            name="userId"
            :required="isRequired(schema, 'userId')"
          >
            <SelectMenuBaseReference
              v-model="state"
              clearable
              v-bind="{
                tableName: 'users',
                idField: 'userId',
                utils: utils,
                options: utils.options.userId.value ?? [],
                disabled: disabledFields?.includes('userId'),
              }"
            />
          </UFormGroup>
        </template>

        <template #details>
          <UFormGroup
            label="Estado"
            name="status"
            :required="isRequired(schema, 'status')"
          >
            <SelectMenuBaseInfo
              v-model="state.status"
              :options="utils.options.status"
              :disabled="disabledFields?.includes('status')"
            />
          </UFormGroup>

          <UFormGroup
            label="Fecha de recepción"
            name="date"
            :required="isRequired(schema, 'date')"
          >
            <InputDatetime
              v-model:date="state.date"
              v-model:is-datetime="state.isDatetime"
              :disabled="disabledFields?.includes('date')"
            />
          </UFormGroup>

          <UFormGroup
            label="Comprobante"
            name="fileProofPdf"
            :required="isRequired(schema, 'fileProofPdf')"
          >
            <InputFile
              v-model="state.fileProofPdf"
              table-name="salesorders"
              :record-id="state.id"
              filename="comprobante.pdf"
              accept="application/pdf"
              :disabled="disabledFields?.includes('fileProofPdf')"
            />
          </UFormGroup>

          <UFormGroup
            label="Observaciones"
            name="notes"
            :required="isRequired(schema, 'notes')"
            hint="(Observaciones se reflejarán en la orden de venta)"
          >
            <InputStrings v-model="state.notes" placeholder="Ingresa una nueva observación" />
          </UFormGroup>

          <UFormGroup label="Etiquetas" name="tags">
            <SelectMenuCreatableString
              v-model="state.tags"
              :options="state.tags"
              creatable
            />
          </UFormGroup>

          <UFormGroup label="Archivos" name="files">
            <InputFiles
              v-model="state.files"
              table-name="salesorders"
              :record-id="state.id"
              accept="*"
              :required="['salesorder', 'payslip'].includes(state.type)"
            />
          </UFormGroup>

          <UFormGroup
            label="Comentarios"
            name="text"
            :required="isRequired(schema, 'text')"
          >
            <UTextarea
              v-model="state.text"
              autoresize
              :disabled="disabledFields?.includes('text')"
            />
          </UFormGroup>
        </template>

        <template #payment>
          <FieldsetPayment
            v-model="state.payment"
            :schema="schema"
            :disabled-fields="disabledFields"
          />
        </template>

        <template #delivery>
          <FieldsetDelivery
            v-model="state"
            :disabled="!state.accountId"
            v-bind="{
              utils,
              disabledFields,
              schema,
              refresh: $branches.refresh,
              openNestedCallback: (data) => openNestedCallback({ ...props, data }).then(refresh),
            }"
          />
        </template>

        <template #data>
          <FieldsetData v-model="state.data" :utils="utils" />
        </template>

        <template #pdfSettings>
          <FieldsetPdfSettings v-model="state!.pdfSettings" :cols="2" />
        </template>
      </UAccordion>
    </UForm>

    <template v-if="!inert" #footer>
      <div class="grid grid-cols-2 gap-2 w-full">
        <UButton
          color="gray"
          label="Cancelar"
          block
          @click="slideover.close()"
        />
        <UButton
          form="form.salesorders"
          type="submit"
          label="Confirmar"
          color="primary"
          block
          :loading="loading"
        />
      </div>
    </template>
  </UDashboardSlideover>
</template>
